import { Button, Icon } from '@yembo/yemblocks-core';
import { memo, useState } from 'react';
import AudioPlayer from 'src/react/TextMessages/AudioPlayer/AudioPlayer';
import { TextMessage } from 'src/types';
import { downloadFile } from '../_utils/downloadFile';
import { ImageAttachment } from '../ImageAttachment';

type Props = {
  message: TextMessage;
  insuredName: string;
  isCopiedFromOriginalJob: boolean;
};

const areEqual = (prevProps: Props, nextProps: Props): boolean => {
  const propKeys = Object.keys(nextProps) as (keyof typeof nextProps)[];
  return propKeys.every((key) => {
    if (key === 'message') {
      return prevProps.message.key === nextProps.message.key;
    }
    return nextProps[key] === prevProps[key];
  });
};

export const MessageItem = memo(({ message, insuredName, isCopiedFromOriginalJob }: Props): React.JSX.Element => {
  const [downloadQueue, setDownloadQueue] = useState<string[]>([]);

  const onDownload = async (fileUrl: string) => {
    setDownloadQueue([...downloadQueue, fileUrl]);
    await downloadFile(fileUrl, insuredName);
    setDownloadQueue(downloadQueue.filter((url) => url !== fileUrl));
  };

  const formatMessage = (text: string) => {
    const formattedText = text.split('\n').map((messageLine, index) => {
      return (
        <span key={index}>
          {messageLine}
          <br />
        </span>
      );
    });
    return formattedText;
  };

  const renderImages = () => {
    const { images: messageImages, senderType } = message;
    if (!messageImages?.length) {
      return null;
    }

    return messageImages.map((image) => (
      <ImageAttachment
        key={image}
        url={image}
        isOverlayEnabled={senderType === 'insured' || isCopiedFromOriginalJob}
        onDownload={onDownload}
        downloadQueue={downloadQueue}
      />
    ));
  };

  const renderVideos = () => {
    const { videos } = message;
    if (!videos?.length) {
      return null;
    }

    return videos.map((video) => {
      return (
        <div key={video} onClick={() => onDownload(video)} className='media-item'>
          <Button
            kind='flat'
            className='download-video-button'
            onClick={() => onDownload(video)}
            isLoading={downloadQueue.includes(video)}
            icon={<Icon name='download' />}
            size='small'
          />
          <video className='video-message' controls src={video} />
        </div>
      );
    });
  };

  const renderAudios = () => {
    const { audio } = message;
    if (!audio?.length) {
      return null;
    }

    return audio.map((audioItem) => {
      return (
        <div className='media-item' key={audioItem}>
          <AudioPlayer source={audioItem} onDownload={onDownload} downloadQueue={downloadQueue} />
        </div>
      );
    });
  };

  const messageText = formatMessage(message.message);

  return (
    <div className='chat-message'>
      {renderImages()}
      {renderAudios()}
      {renderVideos()}
      {message.message ? <p className='text-wrapper'>{messageText}</p> : null}
    </div>
  );
}, areEqual);
